import { datadogRum } from '@datadog/browser-rum';
import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { RouterClient } from '@orpc/server';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import type { AppRouter } from 'mortis/router';

import { toast } from '@/hooks/useToast';
import { logger } from '@/lib/logger';

import { authClient } from './auth';
import { clearDatadogUser } from './datadog';
import { env } from './env';

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isAbortError(error)) {
        return;
      }

      toast.error(`Error: ${error.message}`, {
        action: {
          label: 'retry',
          onClick: () => {
            queryClient.invalidateQueries();
          },
        },
      });
    },
  }),
});

export const link = new RPCLink({
  url: `${env.NEXT_PUBLIC_API_URL}/api`,
  async fetch(req, options) {
    const urlObj = new URL(req.url);

    try {
      const sessionId = datadogRum.getInternalContext()?.session_id;
      const headers = new Headers(req.headers);
      if (sessionId) headers.set('x-dd-session-id', sessionId);
      const modifiedReq = new Request(req, { headers });

      const response = await fetch(modifiedReq, {
        ...options,
        credentials: 'include',
      });

      if (response.status === 401) {
        logger.warn('401 Unauthorized - triggering logout', {
          url: urlObj.pathname,
        });
        const session = await authClient.getSession();
        if (!session?.data?.session) {
          await authClient.signOut();
          clearDatadogUser();
          queryClient.clear();
          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            // Skip when we're already on an auth page. Otherwise an in-flight
            // ORPC request that 401s after the user has already navigated to
            // /sign-up (e.g. logout race) clobbers the existing returnTo with
            // a self-referential one (returnTo=/sign-up), which then propagates
            // through /verify-account and dumps the user back on /sign-up
            // post-OTP instead of taking them home.
            if (
              currentPath !== '/sign-up' &&
              currentPath !== '/verify-account'
            ) {
              window.location.href = `/sign-up?returnTo=${encodeURIComponent(currentPath)}`;
            }
          }
        }
      }

      if (!response.ok) {
        logger.warn('ORPC request failed', {
          url: urlObj.pathname,
          status: response.status,
        });
      }

      return response;
    } catch (error) {
      if (isAbortError(error)) {
        logger.debug('ORPC request aborted', {
          url: urlObj.pathname,
          method: req?.method,
          tags: { service: 'orpc-client', layer: 'frontend' },
        });
        throw error;
      }

      logger.error('ORPC request error', {
        error: error as Error,
        url: urlObj.pathname,
        method: req?.method,
        tags: { service: 'orpc-client', layer: 'frontend' },
      });
      throw error;
    }
  },
});

export const client: RouterClient<AppRouter> = createORPCClient(link);

export const orpc = createTanstackQueryUtils(client);
